import { OnInit, Input, OnDestroy, Component, Output, EventEmitter, Inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ReplaySubject, catchError, of, takeUntil, throwError, timeout } from 'rxjs';
import { USE_VERIFICATION_MANUAL_LIGHT, WS_SERVICE_FACTORY } from 'types';
import { PassportData, MRZhandler } from '../pass-verify/passportData';
import { CameraServiceBase } from '../../service/camera.service';
import { CameraInfo, HW_SERVICE_MOCK, CameraType } from '../../service/hw-service-model';
import { AppContext } from '../../base/control/context';
import { CameraSettings, ComponentType } from '../cam.auto.connect';
import { AutoAction, BatteryAutoActionHelper } from '../auto.action';
import { SystemService, SystemServiceFactory } from '../../service/system.service';
import { HWServiceConfig } from '../../service/hw-service-config';
import { cameraServiceFactory } from '../../service/hw-service-factory';
import {
  BuiltinOpticScannerService,
  MrzReaderAdapter,
  MrzReaderAdapterFactory,
  OpticScannerService,
} from '../../service/optic-scanner.service';

@Component({
  selector: 'i-pass-scan-mrz',
  templateUrl: './pass.scan.mrz.component.html',
  styleUrls: ['./pass.scan.mrz.component.scss'],
  providers: [
    {
      provide: CameraServiceBase,
      useFactory: cameraServiceFactory,
      deps: [HW_SERVICE_MOCK, HWServiceConfig, WS_SERVICE_FACTORY, MatDialog],
    },
    { provide: OpticScannerService, useClass: BuiltinOpticScannerService },
    { provide: MrzReaderAdapterFactory, useClass: MrzReaderAdapterFactory },
  ],
})
export class PassScanMrzComponent implements OnInit, OnDestroy {
  @Input() activationSingleMode: boolean;
  @Input() stationaryMode: boolean;
  @Output() loadingMRZ = new EventEmitter<string>();
  @Output() printedData = new EventEmitter<any>();
  @Output() isMRZScanVisible = new EventEmitter<MRZVisibleState>();
  @Output() scanMRZStateChange = new EventEmitter<ScanMRZState>();

  private dead: ReplaySubject<void> = new ReplaySubject();
  private autoCancel = new AutoAction(
    (text) => (this.TIME = text),
    () => this.cancel(),
  );
  private selectedCamera: CameraInfo | null = null;
  private mrzReader: MrzReaderAdapter;
  private systemService: SystemService;
  private batteryHelper: BatteryAutoActionHelper;
  private mrzScanTimeoutMilliseconds: number = 30000;

  scanMRZState: ScanMRZState = ScanMRZState.Closed;
  TIME: string = '';
  camList: CameraInfo[] = [];
  rotateToogle: boolean = true;
  ligthsOn: boolean = false;

  constructor(
    private appContext: AppContext,
    private mrzReaderAdapterFactory: MrzReaderAdapterFactory,
    private systemServiceFactory: SystemServiceFactory,
    private config: HWServiceConfig,
    @Inject(USE_VERIFICATION_MANUAL_LIGHT) public useVerificationManualLight: boolean,
  ) {
    this.mrzReader = this.mrzReaderAdapterFactory.create();
    if (this.mrzReader.batterySupport()) {
      this.systemService = this.systemServiceFactory.create(this.config);
      this.batteryHelper = new BatteryAutoActionHelper(this.autoCancel, this.systemService, this.dead);
    }
  }

  ngOnInit() {
    this.startScaning();
  }

  startScaning() {
    this.isMRZScanVisible.emit(MRZVisibleState.visible);
    this.scanMRZState = ScanMRZState.InitializationInProgress;
    this.scanMRZStateChange.emit(this.scanMRZState);
    if (this.systemService && this.batteryHelper) {
      this.batteryHelper.run();
    }

    this.mrzReader = this.mrzReaderAdapterFactory.create();
    this.mrzReader
      .init()
      .pipe(takeUntil(this.dead))
      .subscribe(
        (res) => {
          this.camList = res;
          this.selectedCamera = CameraSettings.selectCamera(ComponentType.MrzScanner, this.camList, CameraType.Back);
          if (this.selectedCamera) {
            this.startMRZscan();
          } else {
            this.handleError('Не найдено ни одной камеры для считывания MRZ', ScanMRZState.InitializationError);
          }
        },
        (error) => this.handleError(error, ScanMRZState.InitializationError),
      );
  }

  ngOnDestroy() {
    this.dead.next();
    this.dead.complete();
    this.mrzReader.done().subscribe();
    if (this.systemService) {
      this.systemService.disconnect().subscribe();
    }
  }

  get isInitializationScreenVisible$(): boolean {
    return this.scanMRZState === ScanMRZState.InitializationInProgress;
  }
  get isInitializationErrorScreenVisible$(): boolean {
    return this.scanMRZState === ScanMRZState.InitializationError;
  }
  get rotateCameraScreen$(): boolean {
    return this.scanMRZState === ScanMRZState.RotateCameraScreen;
  }
  get isShootingScreenVisible$(): boolean {
    return (
      this.scanMRZState !== ScanMRZState.InitializationInProgress &&
      this.scanMRZState !== ScanMRZState.InitializationError &&
      this.scanMRZState !== ScanMRZState.ShutdownInProgress &&
      this.scanMRZState !== ScanMRZState.Closed
    );
  }

  cancel() {
    this.scanMRZState = ScanMRZState.Closed;
    this.scanMRZStateChange.emit(this.scanMRZState);
    this.isMRZScanVisible.emit(MRZVisibleState.closed);
  }

  rotate() {
    this.scanMRZState = ScanMRZState.RotateCameraScreen;
    this.scanMRZStateChange.emit(this.scanMRZState);
    this.mrzReader
      .stopReading()
      .pipe(takeUntil(this.dead))
      .subscribe(
        (r) => {
          this.rotateToogle = !this.rotateToogle;
          this.selectedCamera = CameraSettings.selectAnotherCamera(ComponentType.MrzScanner, this.camList, this.selectedCamera!.id);
          this.startMRZscan();
        },
        (error) => this.handleError(error, ScanMRZState.InitializationError),
      );
  }

  private startMRZscan() {
    this.setLightMode(
      this.useVerificationManualLight
        ? CameraSettings.getLightMode(ComponentType.MrzScanner, this.selectedCamera!)
        : CameraSettings.isLightSupported(this.selectedCamera!.type),
    );
    this.mrzReader
      .startReading(this.selectedCamera!)
      .pipe(
        takeUntil(this.dead),
        timeout(this.mrzScanTimeoutMilliseconds),
        catchError((error) => {
          if (error.name === 'TimeoutError') {
            this.handleError(error, ScanMRZState.InitializationError);
            return of(undefined);
          }
          this.handleError(error, ScanMRZState.InitializationError);
          return throwError(() => new Error(error));
        }),
      )
      .subscribe(
        (r) => {
          if (r === undefined) {
            this.scanMRZState = ScanMRZState.Preview;
            this.scanMRZStateChange.emit(this.scanMRZState);
          } else {
            this.reciviedMRZResult(r);
          }
        },
        (error) => this.handleError(error, ScanMRZState.InitializationError),
      );
  }

  private reciviedMRZResult(mrz88: string) {
    this.isMRZScanVisible.emit(MRZVisibleState.invisible);
    this.setLightMode(false);
    const dataPass = new PassportData();
    dataPass.photo = null;
    MRZhandler(this.appContext, dataPass, mrz88);
    this.printedData.emit(dataPass);
    this.loadingMRZ.emit(mrz88.substring(44));
  }

  private handleError(error: any, state: ScanMRZState | null = null): void {
    if (state) {
      this.scanMRZState = state;
      this.scanMRZStateChange.emit(this.scanMRZState);
      this.appContext.getMessageSink()('ERROR', 'PASSPORT.VERIFICATION.ERROR.CONNECT');
    }
  }

  changeLightMode() {
    let enable = !this.ligthsOn;
    this.setLightMode(enable);
    CameraSettings.setLightMode(ComponentType.MrzScanner, enable);
  }

  private setLightMode(enable: boolean) {
    this.ligthsOn = enable;
    this.mrzReader.lights(enable).subscribe();
  }
}

export enum ScanMRZState {
  InitializationInProgress,
  InitializationError,
  ShootingInProgress,
  RotateCameraScreen,
  PhotoProcessingInProgress,
  ShutdownInProgress,
  ShootingResultConfirmation,
  Preview,
  Closed,
}

export enum MRZVisibleState {
  visible,
  invisible,
  closed,
}
